import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import TextareaQueCrece from '../TextareaQueCrece';
import { FileText, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Informe oftalmológico en PDF (4/11/2026): el servidor redacta el borrador
 * con la historia (services/oftalmo_informe.py), aquí se retoca y se crea como
 * un informe normal (/api/informes): con el membrete, la firma y el sello de
 * Configuración, en la pestaña «Informes» y para enviar por WhatsApp.
 */
export default function InformeOftalmo({ pid, onCreado }) {
  const { user } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');

  const abrir = async () => {
    setCargando(true);
    try {
      const r = await api.get(`/api/patients/${pid}/oftalmologia/informe-borrador`);
      setTitulo(r.data.title);
      setCuerpo(r.data.body);
      setAbierto(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el informe');
    } finally {
      setCargando(false);
    }
  };

  const crear = async () => {
    // La pestaña se abre ya (al pulsar), si no el navegador la bloquea.
    const ventana = window.open('', '_blank');
    setCreando(true);
    try {
      const r = await api.post('/api/informes', {
        patient_id: pid, title: titulo, body: cuerpo,
        doctor_name: (user?.role || '').toLowerCase() === 'doctor' ? user.name : '',
      });
      const url = `${BACKEND_URL}/api/informes/${r.data.id}/pdf`;
      if (ventana) ventana.location = url; else window.open(url, '_blank');
      toast.success('Informe creado (también en la pestaña «Informes»)');
      setAbierto(false);
      onCreado?.(r.data);
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo crear el informe');
    } finally {
      setCreando(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={abrir} disabled={cargando} data-testid="oft-informe">
        {cargando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}Informe oftalmológico
      </Button>
      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="oft-informe-dialog">
          <DialogHeader><DialogTitle>Informe oftalmológico · revisa y crea el PDF</DialogTitle></DialogHeader>
          <label className="text-xs text-muted-foreground flex flex-col gap-1">Título
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="h-9" />
          </label>
          <label className="text-xs text-muted-foreground flex flex-col gap-1">Texto (lo que va en el PDF)
            <TextareaQueCrece value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} rows={14} className="text-sm font-mono" />
          </label>
          <div className="text-[11px] text-muted-foreground">
            «## Título» es un apartado, «- …» una línea de lista y **así** va en negrita. Sale con el membrete, la firma y el sello de Configuración.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
            <Button onClick={crear} disabled={creando || !titulo.trim() || !cuerpo.trim()} data-testid="oft-informe-crear">
              {creando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Crear informe y abrir PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
